/**
 * 图片处理工具函数
 */

/**
 * 将二进制流转换为图片URL
 * @param {string} id - 文件ID
 * @returns {Promise<string>} 图片URL
 */
export async function getImageFromBinary(id) {
  try {
    const { GetFile } = await import('@/utils/api');
    const response = await GetFile(id);
    
    // 现在响应拦截器会返回完整的响应对象，所以 response.data 应该是 Blob
    const blob = response.data;
    
    // 检查 blob 是否有效
    if (!blob || !(blob instanceof Blob) || blob.size === 0) {
      throw new Error('图片数据为空或格式不正确');
    }
    
    // 验证是否为有效的图片格式
    if (blob.type && !blob.type.startsWith('image/')) {
      console.warn('文件不是有效的图片格式:', blob.type);
      // 即使类型不匹配，也尝试创建 URL，因为有些服务器可能不设置正确的 Content-Type
    }
    
    // 创建对象URL
    const imageUrl = URL.createObjectURL(blob);
    return imageUrl;
  } catch (error) {
    console.error('获取图片失败:', error);
    // 返回默认图片
    return getDefaultImage();
  }
}

/**
 * 获取默认图片URL
 * @param {string} text - 默认图片文字
 * @param {number} width - 图片宽度
 * @param {number} height - 图片高度
 * @returns {string} 默认图片URL
 */
export function getDefaultImage(text = '暂无图片', width = 300, height = 400) {
  return `https://via.placeholder.com/${width}x${height}?text=${encodeURIComponent(text)}`;
}

/**
 * 预加载图片
 * @param {string} url - 图片URL
 * @returns {Promise<HTMLImageElement>} 图片元素
 */
export function preloadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('图片加载失败'));
    img.src = url;
  });
}

/**
 * 批量预加载图片
 * @param {string[]} urls - 图片URL数组
 * @returns {Promise<HTMLImageElement[]>} 图片元素数组
 */
export async function preloadImages(urls) {
  const promises = urls.map(url => preloadImage(url));
  return Promise.allSettled(promises);
}

/**
 * 清理图片URL，避免内存泄漏
 * @param {string} url - 图片URL
 */
export function revokeImageURL(url) {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

/**
 * 批量清理图片URL
 * @param {string[]} urls - 图片URL数组
 */
export function revokeImageURLs(urls) {
  urls.forEach(url => revokeImageURL(url));
}
